import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { db } from '../services/firebase'
import { collection, getDocs } from 'firebase/firestore'
import { motion } from 'framer-motion'
import SearchBar from '../components/common/SearchBar'
import CenterPitchSlots from '../components/common/CenterPitchSlots'
import { isCenterBookingEnabled } from '../utils/centerAvailability'
import { BuildingOfficeIcon, MapPinIcon, StarIcon } from '@heroicons/react/24/outline'

const SportsCenters = () => {
  const { t } = useTranslation()
  const [centers, setCenters] = useState([])
  const [filteredCenters, setFilteredCenters] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCenter, setSelectedCenter] = useState(null)

  useEffect(() => {
    fetchCenters()
  }, [])

  useEffect(() => {
    if (searchTerm) {
      setFilteredCenters(
        centers.filter(center =>
          center.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          center.address?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      )
    } else {
      setFilteredCenters(centers)
    }
  }, [searchTerm, centers])

  const fetchCenters = async () => {
    try {
      const centersSnapshot = await getDocs(collection(db, 'sportsCenters'))
      const centersData = centersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
      setCenters(centersData)
      setFilteredCenters(centersData)
    } catch (error) {
      console.error('Error fetching centers:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatCenterValue = (value) => {
    if (value === null || value === undefined) return t('centers.notAvailable')
    if (typeof value === 'string' && value.trim().length === 0) return t('centers.notAvailable')
    return value
  }

  const formatBookingStatus = (center) =>
    isCenterBookingEnabled(center) ? t('centers.bookingsAvailable') : t('centers.bookingsUnavailable')

  const formatSports = (sports) => {
    if (!Array.isArray(sports) || sports.length === 0) return t('centers.notAvailable')
    return sports
      .map((sport) => (sport === 'calcio' ? t('sports.calcio') : sport === 'padel' ? t('sports.padel') : sport))
      .join(', ')
  }

  const formatFacilities = (facilities) => {
    if (!Array.isArray(facilities) || facilities.length === 0) return t('centers.notAvailable')
    return facilities.join(', ')
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('centers.title')}</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">{t('centers.description')}</p>
      </div>

      {/* Search */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
        <SearchBar 
          onSearch={setSearchTerm}
          placeholder={t('centers.searchPlaceholder')}
        />
      </div>

      {/* Centers Grid */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block w-8 h-8 border-4 border-sport-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : filteredCenters.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredCenters.map((center, index) => (
            <motion.div
              key={center.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition"
            >
              {center.images?.[0] && (
                <img
                  src={center.images[0]}
                  alt={center.name}
                  className="w-full h-48 object-cover"
                />
              )}
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">{center.name}</h3>
                    <div className="flex items-center space-x-1 mt-1">
                      <StarIcon className="h-4 w-4 text-yellow-400" />
                      <span className="text-sm text-gray-600">{center.rating || t('common.new')}</span>
                    </div>
                  </div>
                  <div className="flex space-x-1">
                    {center.sports?.map((sport, idx) => (
                      <span key={idx} className="text-2xl">
                        {sport === 'calcio' ? '⚽' : '🎾'}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mt-4 space-y-2">
                  <div className="flex items-center text-gray-600 dark:text-gray-400">
                    <MapPinIcon className="h-4 w-4 mr-2" />
                    <span className="text-sm">{center.address}</span>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex flex-wrap gap-2">
                    {center.facilities?.map((facility, idx) => (
                      <span key={idx} className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                        {facility}
                      </span>
                    ))}
                  </div>
                </div>

                <CenterPitchSlots center={center} variant="card" />

                <button
                  onClick={() => setSelectedCenter(center)}
                  className="mt-4 w-full btn-outline"
                >
                  {t('centers.viewDetails')}
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl p-12 text-center">
          <BuildingOfficeIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">{t('centers.noCentersFound')}</p>
        </div>
      )}

      {selectedCenter && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => setSelectedCenter(null)}
        >
          <div 
            className="w-full max-w-md sm:max-w-lg rounded-xl bg-white dark:bg-gray-800 shadow-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {selectedCenter.images?.[0] && (
              <img
                src={selectedCenter.images[0]}
                alt={selectedCenter.name}
                className="h-40 sm:h-56 w-full rounded-t-xl object-cover"
              />
            )}

            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">{selectedCenter.name}</h3>
                  <div className="mt-1 flex items-center space-x-1 text-sm text-gray-600 dark:text-gray-300">
                    <StarIcon className="h-4 w-4 text-yellow-400" />
                    <span>{selectedCenter.rating || t('common.new')}</span>
                  </div>
                </div>
                <div className="flex gap-1">
                  {selectedCenter.sports?.map((sport, idx) => (
                    <span key={idx} className="text-xl">
                      {sport === 'calcio' ? '⚽' : '🎾'}
                    </span>
                  ))}
                </div>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-2 sm:gap-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 sm:p-4">
                {/* Left Column */}
                <div className="space-y-2 sm:space-y-3">
                  <div>
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">{t('centers.details.centerName')}</p>
                    <p className="text-xs sm:text-base text-gray-900 dark:text-white mt-1">{formatCenterValue(selectedCenter.name)}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">{t('centers.details.address')}</p>
                    <p className="text-xs sm:text-base text-gray-900 dark:text-white mt-1">{formatCenterValue(selectedCenter.address)}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">{t('centers.details.city')}</p>
                    <p className="text-xs sm:text-base text-gray-900 dark:text-white mt-1">{formatCenterValue(selectedCenter.city)}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">{t('centers.details.phone')}</p>
                    <p className="text-xs sm:text-base text-gray-900 dark:text-white mt-1">{formatCenterValue(selectedCenter.phone)}</p>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-2 sm:space-y-3">
                  <div>
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">{t('centers.details.email')}</p>
                    <p className="text-xs sm:text-base text-gray-900 dark:text-white mt-1 break-all">{formatCenterValue(selectedCenter.email)}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">{t('centers.details.pricePerHour')}</p>
                    <p className="text-xs sm:text-base text-gray-900 dark:text-white mt-1">{formatCenterValue(selectedCenter.pricePerHour)}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">{t('centers.details.bookings')}</p>
                    <p
                      className={`text-xs sm:text-base mt-1 font-medium ${
                        isCenterBookingEnabled(selectedCenter)
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-gray-500 dark:text-gray-400'
                      }`}
                    >
                      {formatBookingStatus(selectedCenter)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Additional Info Grid */}
              <div className="grid grid-cols-2 gap-2 sm:gap-4">
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 sm:p-4">
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1 sm:mb-2">{t('centers.details.availableSports')}</p>
                  <p className="text-xs sm:text-base text-gray-900 dark:text-white">{formatSports(selectedCenter.sports)}</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 sm:p-4">
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1 sm:mb-2">{t('centers.details.facilities')}</p>
                  <p className="text-xs sm:text-base text-gray-900 dark:text-white">{formatFacilities(selectedCenter.facilities)}</p>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 sm:p-4">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1 sm:mb-2">{t('centers.details.description')}</p>
                <p className="text-xs sm:text-base text-gray-900 dark:text-white">{formatCenterValue(selectedCenter.description)}</p>
              </div>

              <CenterPitchSlots center={selectedCenter} variant="modal" />

              <button
                onClick={() => setSelectedCenter(null)}
                className="mt-2 w-full btn-outline"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SportsCenters